import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLaadpleinVerkeersdata } from '@/hooks/useLaadpleinVerkeersdata';
import { DataSourceBadge } from './DataSourceBadge';

// Deze tab toonde 453 dagen lang vaste constanten als vrachtwagenaantallen; het
// enige verschil met echte data was een grijze badge. De herkomst staat nu bij
// elk getal én in een banner die je niet kunt missen. Zie de kop van
// src/hooks/useLaadpleinVerkeersdata.ts voor de metingen erachter.

export function CorridorsTab() {
  const [sort, setSort] = useState<'trucks' | 'weg'>('trucks');
  const { corridors, herkomst, isCorridorLive } = useLaadpleinVerkeersdata();

  const sorted = [...corridors].sort((a, b) =>
    sort === 'trucks' ? b.trucks_dag - a.trucks_dag : a.weg.localeCompare(b.weg)
  );
  const maxT = Math.max(...corridors.map(c => c.trucks_dag), 1);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-500">Vrachtverkeer op snelwegen regio Eindhoven/Den Bosch (werkdaggemiddelde)</div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSort('trucks')}
            className={sort === 'trucks' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-400'}
          >
            Op volume
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSort('weg')}
            className={sort === 'weg' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-400'}
          >
            Op weg
          </Button>
        </div>
      </div>

      {/* De terugval op constanten is hier het hoofdbericht, geen voetnoot. */}
      {herkomst.totaal > 0 && herkomst.gemeten < herkomst.totaal && (
        <div
          className={`flex gap-3 rounded-xl border p-4 mb-4 ${
            herkomst.allesAanname
              ? 'bg-amber-50 border-amber-300'
              : 'bg-amber-50/60 border-amber-200'
          }`}
        >
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-amber-900">
              {herkomst.allesAanname
                ? 'Deze cijfers zijn niet gemeten'
                : `${herkomst.aanname} van de ${herkomst.totaal} cijfers zijn niet gemeten`}
            </div>
            <div className="text-xs text-amber-800 mt-1">{herkomst.tekst}</div>
            <div className="text-xs text-amber-700 mt-1">
              De NDW-koppeling is nooit in gebruik genomen: er staat geen verkeersrecord in de database, er
              draait geen NDW-synchronisatie, en de geautomatiseerde import vraagt om NDW-credentials die
              niet zijn ingesteld. Gebruik deze getallen als aanname, niet als meting.
            </div>
          </div>
        </div>
      )}

      <div className="space-y-1.5 mb-6">
        {sorted.map(c => {
          const live = isCorridorLive(c.weg, c.richting);
          const ev2027 = Math.round(c.trucks_dag * 0.07);
          const ev2028 = Math.round(c.trucks_dag * 0.114);
          return (
            <Card key={c.id} className="hover:border-emerald-300 transition-colors">
              <CardContent className="p-3">
                <div className="flex justify-between items-start mb-1.5">
                  <div className="flex items-center flex-wrap gap-x-1">
                    <span className="inline-block w-12 text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded mr-1">{c.weg}</span>
                    <span className="text-sm">{c.traject}</span>
                    <span className="text-xs text-gray-400 ml-1">({c.richting})</span>
                    <DataSourceBadge
                      bron={c.gemeten ? c.herkomst : 'schatting'}
                      lastSync={c.gemeten ? c.peildatum : null}
                      isLive={c.gemeten}
                      className="ml-2"
                    />
                  </div>
                  <div className="text-right">
                    {/* Een aanname krijgt een tilde en een doffe kleur: het getal
                        zelf verraadt zijn herkomst, niet alleen een badge. */}
                    <div className={`text-lg font-bold ${c.gemeten ? 'text-gray-800' : 'text-gray-400'}`}>
                      {c.gemeten ? '' : '~'}{c.trucks_dag.toLocaleString('nl-NL')}
                    </div>
                    <div className="text-xs text-gray-400">
                      trucks/dag · {c.gemeten ? c.herkomst_label : 'aanname'}
                    </div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full mb-2">
                  <div
                    className={`h-full rounded-full ${c.gemeten ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    style={{ width: `${(c.trucks_dag / maxT) * 100}%` }}
                  />
                </div>
                <div className="flex gap-4 text-xs text-gray-500 flex-wrap">
                  <span>2027: ~<strong className={c.gemeten ? 'text-emerald-600' : 'text-gray-500'}>{ev2027}</strong> EV/dag (7%)</span>
                  <span>2028: ~<strong className={c.gemeten ? 'text-emerald-600' : 'text-gray-500'}>{ev2028}</strong> EV/dag (11,4%)</span>
                  <span>Bij 12% stopt: <strong className={c.gemeten ? 'text-emerald-700' : 'text-gray-500'}>{Math.round(ev2028 * 0.12)}</strong> sessies/dag</span>
                  {!c.gemeten && <span className="text-amber-700">afgeleid van een aanname</span>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="text-sm font-medium text-gray-800 mb-1">Herkomst van deze cijfers</div>
        <div className="text-xs text-gray-600">
          {herkomst.tekst} De aannames zijn indicatief, gebaseerd op openbare NDW-intensiteitsdata en
          sectorrapportages uit die periode, en staan als vaste waarden in <code>src/constants/laadplein-data.ts</code>.
          Voertuigcategorie N2+N3 = vrachtwagens &gt;3.500&nbsp;kg. Voor echte meetwaarden per meetpunt is een
          NDW Dexter-account nodig (aan te vragen bij de NDW Servicedesk) plus meetpunt-ID&apos;s op de locatie;
          zolang die er niet zijn blijft dit scherm aannames tonen en zegt het dat er ook bij.
        </div>
      </div>
    </div>
  );
}
